import { HttpClient } from '@angular/common/http';
import { Injectable, NgModule, computed, inject, isSignal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  provideTransloco,
  provideTranslocoFallbackStrategy,
  provideTranslocoMissingHandler,
  Translation,
  TranslocoLoader,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { FORMA_PAGINATION_DEFAULTS, FORMA_SELECT_DEFAULTS, FORMA_TRANSLATE } from 'forma';
import { ConfigurationService } from 'app/shared';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import {CustomMissingHandler, CustomStrategy, ShowTranslationKeyService} from './custom-fallback-strategy';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);
  private configurationService = inject(ConfigurationService);

  cachedTranslationFiles: Record<string, Observable<Translation>> = {};

  getTranslation(lang: string) {
    const resolvedLang = lang?.replace('/', '');
    if (!this.cachedTranslationFiles[resolvedLang]) {
      this.cachedTranslationFiles[resolvedLang] = this.http.get<Translation>(`${this.configurationService.staticTranslationUrl}/api/translations`, { params: { code: resolvedLang, fallbackCode: TranslocoConfig.defaultLang, namespace: 'INSIGHT' }, headers: { 'init': 'true' } })
        .pipe(shareReplay(1));
    }
    return this.cachedTranslationFiles[resolvedLang];
  }
}

export const TranslocoConfig = {
  availableLangs: ['ar', 'bg', 'cs', 'da', 'de', 'el', 'en', 'es', 'et', 'fa', 'fi', 'fr', 'gu', 'hi', 'hr', 'hu', 'id', 'is', 'it', 'ja', 'kn', 'ko', 'ku', 'lt', 'lv',
    'ms', 'nl', 'no', 'pl', 'pt', 'pt-br', 'ro', 'ru', 'sk', 'sl', 'so', 'sq', 'sv', 'th', 'tl', 'tr', 'uk', 'vi', 'zh-cn'],
  defaultLang: 'en',
  fallbackLang: ['en'],
  failedRetries: 0,
  missingHandler: {
    logMissingKey: false,
    useFallbackTranslation: true,
    allowEmpty: true,
  },
  reRenderOnLangChange: true,
  prodMode: environment.production,
};
@NgModule({
  exports: [TranslocoModule],
  providers: [
    ShowTranslationKeyService,
    provideTransloco({ config: TranslocoConfig, loader: TranslocoHttpLoader }),
    provideTranslocoMessageformat(),
    provideTranslocoLocale({
      langToLocaleMapping: {
        ko: 'ko-KO',
        ar: 'ar-AE',
        bg: 'bg-BG',
        cs: 'cs-CZ',
        da: 'da-DK',
        de: 'de-DE',
        el: 'el-GR',
        en: 'en-GB',
        es: 'es-ES',
        et: 'et-EE',
        fa: 'fa-IR',
        fi: 'fi-FI',
        fr: 'fr-FR',
        gu: 'gu-IN',
        hi: 'hi-IN',
        hr: 'hr-HR',
        hu: 'hu-HU',
        id: 'id-ID',
        is: 'is-IS',
        it: 'it-IT',
        ja: 'ja-JP',
        kn: 'kn-IN',
        ku: 'ku-KRM',
        lt: 'lt-LT',
        lv: 'lv-LV',
        ms: 'ms-ms',
        nl: 'nl-NL',
        no: 'nn-NO',
        pl: 'pl-PL',
        pt: 'pt-PT',
        'pt-br': 'pt-BR',
        ro: 'ro-RO',
        ru: 'ru-RU',
        sk: 'sk-SK',
        sl: 'sl-SL',
        so: 'so-SO',
        sq: 'sq-AL',
        sv: 'sv-SE',
        tr: 'tr-TR',
        th: 'th-TH',
        uk: 'uk-UK',
        vi: 'vi-VN',
        zh: 'zh-CN',
      },
    }),
    provideTranslocoFallbackStrategy(CustomStrategy),
    provideTranslocoMissingHandler(CustomMissingHandler),
    // Bridge Forma's generic translate hook to Transloco. Keys resolve through
    // Transloco; literal strings pass through unchanged (see CustomMissingHandler).
    {
      provide: FORMA_TRANSLATE,
      useFactory: () => {
        const transloco = inject(TranslocoService);
        const lang = toSignal(transloco.langChanges$, { initialValue: transloco.getActiveLang() });
        return (key, params) => {
          const translationKey = isSignal(key) ? key : signal(key);
          return computed(() => {
            lang();
            return transloco.translate(translationKey(), params);
          });
        };
      },
    },
    // App-wide Forma select defaults (resolved via FORMA_TRANSLATE above).
    {
      provide: FORMA_SELECT_DEFAULTS,
      useValue: {
        placeholder: 'INSIGHT.GLOBAL.SELECT',
        moreText: 'INSIGHT.GLOBAL.MORE',
        searchPlaceholder: 'INSIGHT.PORTAL.PLACEHOLDER_SEARCH',
      },
    },
    // App-wide Forma pagination defaults. English literals until translation keys exist for them.
    {
      provide: FORMA_PAGINATION_DEFAULTS,
      useValue: {
        pageInfo: 'INSIGHT.GLOBAL.PAGER.PAGE_INFO',
        pageInfoShort: 'Page {{currentPage}} of {{totalPages}}',
        pageSizeLabel: 'INSIGHT.GLOBAL.PAGER.ITEMS_PER_PAGE',
        navigationLabel: 'Pagination',
        firstPage: 'First page',
        previousPage: 'INSIGHT.GLOBAL.PREVIOUS',
        nextPage: 'INSIGHT.GLOBAL.NEXT',
        lastPage: 'Last page',
      },
    },
  ],
})
export class TranslocoRootModule {}
